<script setup lang="ts">
import { MiniCartProps } from "./TheMiniCart.props"
import { customPaths } from "~/utils/constants"
import { pushCartClickCtaEvent } from "~/utils/analytics/clickCta"
import { skusCount } from "~/utils/orders/skusCount"
import {
  VIEW_ITEM_LIST_IDS,
  VIEW_ITEM_LIST_NAMES
} from "~/utils/analytics/viewItemList"

const props = defineProps<MiniCartProps>()

const { cart, shippingInfos } = useCart()

const freeShippingRemainingAmount = computed(() => {
  const freeShipping = shippingInfos.shippingInfos.value?.freeShipping
  if (typeof freeShipping === "number") {
    return freeShipping
  }
  return freeShipping === true ? 0 : undefined
})

const freeShippingThreshold = computed(
  () =>
    shippingInfos.shippingInfos.value?.shippingMethod?.free_over_amount_float
)

const emit =
  defineEmits<{
    (e: "onClose"): void
    (
      e: "onRemoveFromCart",
      payload: { id: string; quantity: number; productCode: string }
    ): void
  }>()

const handleGtm = (label: string) => {
  pushCartClickCtaEvent(label)
}

const handleGoToCartOrCheckout = (label: string) => {
  emit("onClose")
  handleGtm(label)
}

const cartSubtotal = computed(
  () => cart.value?.subtotal_amount_float ?? undefined
)
const cartTotal = computed(() => cart.value?.total_amount_float ?? undefined)
const { promoGiftData, fetchPromoGift } = usePromoGift(cartSubtotal, cartTotal)

onMounted(() => {
  fetchPromoGift()
})
</script>

<template>
  <div class="mini-cart bg-white hidden md:block">
    <div
      class="
        cart-products
        bg-white
        sticky
        top-0
        z-10
        pb-4
        pl-6
        pt-4
        md:-mr-[71px]
        md:pr-[71px]
        lg:-mr-[228px]
        lg:pr-[228px]
      "
    >
      <div class="flex items-center justify-between">
        <div class="flex items-center">
          <h3 class="hyppo-bold">{{ $t("miniCart.myCart") }}</h3>
          <p v-if="!!skusCount(cart)" class="ml-2">
            {{
              $t("miniCart.count", skusCount(cart), {
                count: skusCount(cart)
              })
            }}
          </p>
        </div>
        <UtilsIcon
          name="Close.svg"
          @click="$emit('onClose')"
          class="h-6 w-6 cursor-pointer"
        />
      </div>
    </div>

    <div
      class="
        bg-white
        pb-8
        pl-6
        md:-mr-[71px]
        md:pr-[71px]
        lg:-mr-[228px]
        lg:pr-[228px]
      "
    >
      <CartUtilsPromoGiftProgressBar
        v-if="promoGiftData?.isMinimumReached"
        :promo-gift-data="promoGiftData"
        class="mb-2"
      />
      <div v-if="cartProducts?.length">
        <CartUtilsFreeShippingLoader
          class="mb-2"
          :remaining-amount="freeShippingRemainingAmount"
          :threshold-amount="freeShippingThreshold"
        />
        <ProductTileCartPreviewCard
          v-for="(product, i) in cartProducts"
          :class="{ 'mb-4 lg:mb-6': i !== cartProducts.length - 1 }"
          :key="`cart-product-${product.productCode}`"
          v-bind="product"
          @onRemoveFromCart="
            $emit('onRemoveFromCart', {
              id: product.id,
              quantity: product.quantity,
              productCode: product.productCode
            })
          "
          @on-close="$emit('onClose')"
        />
      </div>
      <div v-else>
        <div class="flex flex-col items-center gap-4">
          <UtilsIcon name="EmptyCart.svg" class="h-auto w-[128px]" />

          <h4 class="pig-bold md:mammoth-bold">
            {{ $t("cart.whereToStartSection.title") }}
          </h4>
        </div>
      </div>
    </div>

    <div
      class="
        recommended-products
        bg-grey-main
        pb-8
        pl-6
        pt-4
        md:-mr-[71px]
        md:pr-[71px]
        lg:-mr-[228px]
        lg:pr-[228px]
      "
    >
      <div v-if="recommendedProducts?.length">
        <h4 class="beaver-bold mb-4">
          {{ $t("miniCart.recommendedProduct") }}
        </h4>
        <div>
          <GAList
            :id="VIEW_ITEM_LIST_IDS.RECOMMENDED_PRODUCTS"
            :name="VIEW_ITEM_LIST_NAMES.MINICART"
            entity="modalCartProducts"
          >
            <GAItem id="minicart-recommended-item" :item="recommendedProducts">
              <GAViewItemList
                :id="VIEW_ITEM_LIST_IDS.RECOMMENDED_PRODUCTS"
                :name="VIEW_ITEM_LIST_NAMES.MINICART"
              />
            </GAItem>
          </GAList>
          <ProductTileCartPreviewCard
            v-for="(product, i) in recommendedProducts"
            :class="{ 'mb-4 lg:mb-6': i !== recommendedProducts.length - 1 }"
            :key="`recommended-product-${i}`"
            :listName="VIEW_ITEM_LIST_NAMES.MINICART"
            v-bind="product"
            :is-free-gift="false"
            @on-close="$emit('onClose')"
          />
        </div>
      </div>
    </div>

    <div
      class="
        cta-container
        bg-white
        sticky
        bottom-0
        py-4
        pl-6
        shadow-01
        md:-mr-[71px]
        md:pr-[71px]
        lg:-mr-[228px]
        lg:pr-[228px]
      "
    >
      <UtilsButton
        theme="outlined-green"
        :to="customPaths.cart"
        @click="
          () =>
            handleGoToCartOrCheckout($t('dialogsAndModals.addToCart.seeCart'))
        "
        :text="$t('dialogsAndModals.addToCart.seeCart')"
        fluid
        class="mb-3 block lg:mb-4"
      />
      <UtilsButton
        :text="$t('dialogsAndModals.addToCart.goToPay')"
        fluid
        :to="cartProducts?.length ? customPaths.checkout : ''"
        @click="
          () =>
            handleGoToCartOrCheckout($t('dialogsAndModals.addToCart.goToPay'))
        "
        :isDisabled="!cartProducts?.length"
      />
    </div>
  </div>
</template>
